/**
 * A memo for a pure function of a string key, bounded by clearing everything
 * once it holds `cap` entries (cheaper than LRU bookkeeping; a clear costs
 * one recompute per key still in use). Caches `null` results too.
 */
export interface BoundedMemo<V> {
  /** The memoized value for `key`, computing and storing it on a miss. */
  get(key: string, compute: (key: string) => V): V;
  clear(): void;
  readonly size: number;
}

export function createBoundedMemo<V>(cap: number): BoundedMemo<V> {
  const entries = new Map<string, V>();
  return {
    get(key, compute) {
      if (entries.has(key)) return entries.get(key) as V;
      const value = compute(key);
      if (entries.size >= cap) entries.clear();
      entries.set(key, value);
      return value;
    },
    clear() {
      entries.clear();
    },
    get size() {
      return entries.size;
    },
  };
}
